import moment from 'moment';
import { getCollapsedDayMidPx } from './getCollapsedDayMidPx';
import { getVisibleDayColumns } from './getVisibleDayColumns';
import { DAY_WIDTH_PX } from './monthlyLayoutConstants';
import type { MonthDescriptor } from './types';

const WEEKEND_BG = 'rgba(217, 217, 217, 0.15)';
// const WEEKEND_BG = 'red';

type DayZone = {
  day: number;
  isWeekend: boolean;
  zoneStartPct: number;
  zoneEndPct: number;
  centerPct: number;
};

function isWeekendDow(dow: number): boolean {
  return dow === 0 || dow === 6;
}

function getDayOfWeek(month: MonthDescriptor, day: number): number {
  return moment({
    year: month.year,
    month: month.month - 1,
    day
  }).day();
}

function getNextCalendarDay(
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[],
  day: number
): { month: MonthDescriptor; day: number } | null {
  if (day < month.daysInMonth) {
    return { month, day: day + 1 };
  }

  if (monthIndex >= months.length - 1) {
    return null;
  }

  const nextMonth = months[monthIndex + 1];
  return { month: nextMonth, day: 1 };
}

function isNextDayWeekend(
  day: number,
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[]
): boolean {
  const next = getNextCalendarDay(month, monthIndex, months, day);
  if (!next) {
    return false;
  }

  return isWeekendDow(getDayOfWeek(next.month, next.day));
}

function getPrevCalendarDay(
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[],
  day: number
): { month: MonthDescriptor; day: number } | null {
  if (day > 1) {
    return { month, day: day - 1 };
  }

  if (monthIndex === 0) {
    return null;
  }

  const prevMonth = months[monthIndex - 1];
  return { month: prevMonth, day: prevMonth.daysInMonth };
}

function toCellPct(
  valuePx: number,
  cellLeftPx: number,
  dayWidthPx: number
): number {
  return ((valuePx - cellLeftPx) / dayWidthPx) * 100;
}

function getExpandedDayZones(
  columnDay: number,
  colSpan: number,
  month: MonthDescriptor
): DayZone[] {
  if (colSpan === 1) {
    const dow = getDayOfWeek(month, columnDay);
    return [
      {
        day: columnDay,
        isWeekend: isWeekendDow(dow),
        zoneStartPct: 0,
        zoneEndPct: 100,
        centerPct: 50
      }
    ];
  }

  const leftDow = getDayOfWeek(month, columnDay);
  const zones: DayZone[] = [
    {
      day: columnDay,
      isWeekend: isWeekendDow(leftDow),
      zoneStartPct: 0,
      zoneEndPct: 50,
      centerPct: 25
    }
  ];

  const rightDay = columnDay + 1;
  if (rightDay <= month.daysInMonth) {
    const rightDow = getDayOfWeek(month, rightDay);
    zones.push({
      day: rightDay,
      isWeekend: isWeekendDow(rightDow),
      zoneStartPct: 50,
      zoneEndPct: 100,
      centerPct: 75
    });
  }

  return zones;
}

function getCollapsedDayZones(
  columnDay: number,
  colSpan: number,
  month: MonthDescriptor,
  visibleOddDays: number[],
  dayWidthPx: number
): DayZone[] {
  const columnIndex = visibleOddDays.indexOf(columnDay);
  if (columnIndex < 0) {
    return [];
  }

  const cellLeftPx = columnIndex * dayWidthPx;
  const lastDayInCell = Math.min(columnDay + colSpan - 1, month.daysInMonth);
  const zones: DayZone[] = [];

  for (let day = columnDay; day <= lastDayInCell; day += 1) {
    const centerPx = getCollapsedDayMidPx(day, visibleOddDays, dayWidthPx);
    const centerPct = toCellPct(centerPx, cellLeftPx, dayWidthPx);

    let zoneStartPct = 0;
    let zoneEndPct = 100;

    if (day > 1) {
      const prevCenterPx = getCollapsedDayMidPx(
        day - 1,
        visibleOddDays,
        dayWidthPx
      );
      zoneStartPct = Math.max(
        0,
        toCellPct((prevCenterPx + centerPx) / 2, cellLeftPx, dayWidthPx)
      );
    }

    if (day < month.daysInMonth) {
      const nextCenterPx = getCollapsedDayMidPx(
        day + 1,
        visibleOddDays,
        dayWidthPx
      );
      const nextInSameCell = day + 1 <= lastDayInCell;
      zoneEndPct = nextInSameCell
        ? Math.min(
            100,
            toCellPct((centerPx + nextCenterPx) / 2, cellLeftPx, dayWidthPx)
          )
        : 100;
    } else {
      zoneEndPct = 100;
    }

    zones.push({
      day,
      isWeekend: isWeekendDow(getDayOfWeek(month, day)),
      zoneStartPct,
      zoneEndPct,
      centerPct
    });
  }

  return zones;
}

function getDayZones(
  columnDay: number,
  colSpan: number,
  month: MonthDescriptor,
  isExpanded: boolean,
  isCompact: boolean,
  dayWidthPx: number
): DayZone[] {
  if (isExpanded) {
    return getExpandedDayZones(columnDay, colSpan, month);
  }

  const visibleOddDays = getVisibleDayColumns(
    month.daysInMonth,
    false,
    isCompact
  );
  return getCollapsedDayZones(
    columnDay,
    colSpan,
    month,
    visibleOddDays,
    dayWidthPx
  );
}

function isPreviousDayWeekend(
  firstZoneDay: number,
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[]
): boolean {
  const prev = getPrevCalendarDay(month, monthIndex, months, firstZoneDay);
  if (!prev) {
    return false;
  }

  return isWeekendDow(getDayOfWeek(prev.month, prev.day));
}

function buildGradientFromRanges(
  ranges: Array<{ fromPct: number; toPct: number }>
): string | undefined {
  if (!ranges.length) {
    return undefined;
  }

  const stops: string[] = [];

  ranges.forEach(({ fromPct, toPct }) => {
    if (fromPct >= toPct) {
      return;
    }

    if (fromPct > 0) {
      stops.push(`transparent ${fromPct}%`, `${WEEKEND_BG} ${fromPct}%`);
    } else {
      stops.push(`${WEEKEND_BG} 0%`);
    }

    if (toPct < 100) {
      stops.push(`${WEEKEND_BG} ${toPct}%`, `transparent ${toPct}%`);
    } else {
      stops.push(`${WEEKEND_BG} 100%`);
    }
  });

  if (!stops.length) {
    return undefined;
  }

  return `linear-gradient(to right, ${stops.join(', ')})`;
}

function getCollapsedWeekendRunEndPx(
  lastWeekendDay: number,
  month: MonthDescriptor,
  visibleOddDays: number[],
  dayWidthPx: number
): number {
  if (lastWeekendDay >= month.daysInMonth) {
    return visibleOddDays.length * dayWidthPx;
  }

  const centerPx = getCollapsedDayMidPx(
    lastWeekendDay,
    visibleOddDays,
    dayWidthPx
  );
  const nextCenterPx = getCollapsedDayMidPx(
    lastWeekendDay + 1,
    visibleOddDays,
    dayWidthPx
  );

  return (centerPx + nextCenterPx) / 2;
}

function getCollapsedWeekendRunStartPx(
  runStartDay: number,
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[],
  visibleOddDays: number[],
  dayWidthPx: number
): number {
  if (isPreviousDayWeekend(runStartDay, month, monthIndex, months)) {
    const columnIndex = visibleOddDays.indexOf(runStartDay);
    return columnIndex >= 0 ? columnIndex * dayWidthPx : 0;
  }

  return getCollapsedDayMidPx(runStartDay, visibleOddDays, dayWidthPx);
}

function pushCollapsedWeekendRangeClip(
  runFromPx: number,
  runToPx: number,
  cellLeftPx: number,
  cellRightPx: number,
  dayWidthPx: number,
  ranges: Array<{ fromPct: number; toPct: number }>
): void {
  const clipFromPx = Math.max(runFromPx, cellLeftPx);
  const clipToPx = Math.min(runToPx, cellRightPx);

  if (clipFromPx < clipToPx) {
    ranges.push({
      fromPct: ((clipFromPx - cellLeftPx) / dayWidthPx) * 100,
      toPct: ((clipToPx - cellLeftPx) / dayWidthPx) * 100
    });
  }
}

function getCollapsedWeekendRangesForCell(
  columnDay: number,
  colSpan: number,
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[],
  isCompact: boolean,
  dayWidthPx: number
): Array<{ fromPct: number; toPct: number }> {
  const visibleOddDays = getVisibleDayColumns(
    month.daysInMonth,
    false,
    isCompact
  );
  const columnIndex = visibleOddDays.indexOf(columnDay);
  if (columnIndex < 0) {
    return [];
  }

  const cellLeftPx = columnIndex * dayWidthPx;
  const cellRightPx = cellLeftPx + dayWidthPx;
  const ranges: Array<{ fromPct: number; toPct: number }> = [];
  let runStartDay: number | null = null;

  for (let day = 1; day <= month.daysInMonth; day += 1) {
    const isWeekend = isWeekendDow(getDayOfWeek(month, day));

    if (isWeekend && runStartDay === null) {
      runStartDay = day;
    }

    if (isWeekend && !isNextDayWeekend(day, month, monthIndex, months)) {
      pushCollapsedWeekendRangeClip(
        getCollapsedWeekendRunStartPx(
          runStartDay!,
          month,
          monthIndex,
          months,
          visibleOddDays,
          dayWidthPx
        ),
        getCollapsedWeekendRunEndPx(day, month, visibleOddDays, dayWidthPx),
        cellLeftPx,
        cellRightPx,
        dayWidthPx,
        ranges
      );

      runStartDay = null;
    } else if (!isWeekend) {
      runStartDay = null;
    }
  }

  // Weekend run continues into the next month (e.g. Sat Oct 31 → Sun Nov 1).
  if (runStartDay !== null) {
    pushCollapsedWeekendRangeClip(
      getCollapsedWeekendRunStartPx(
        runStartDay,
        month,
        monthIndex,
        months,
        visibleOddDays,
        dayWidthPx
      ),
      visibleOddDays.length * dayWidthPx,
      cellLeftPx,
      cellRightPx,
      dayWidthPx,
      ranges
    );
  }

  return ranges;
}

function getWeekendRangesForZones(
  zones: DayZone[],
  prevDayWasWeekend: boolean,
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[]
): Array<{ fromPct: number; toPct: number }> {
  const ranges: Array<{ fromPct: number; toPct: number }> = [];

  zones.forEach((zone, index) => {
    if (!zone.isWeekend) {
      return;
    }

    const isRunStart =
      index === 0 ? !prevDayWasWeekend : !zones[index - 1].isWeekend;

    const isRunEnd = !isNextDayWeekend(zone.day, month, monthIndex, months);

    const fromPct = isRunStart ? zone.centerPct : zone.zoneStartPct;

    const toPct = isRunEnd
      ? zone.zoneEndPct
      : zones[index + 1]?.zoneStartPct ?? zone.zoneEndPct;

    const lastRange = ranges[ranges.length - 1];
    if (lastRange && lastRange.toPct === fromPct) {
      lastRange.toPct = toPct;
      return;
    }

    ranges.push({ fromPct, toPct });
  });

  return ranges;
}

/**
 * Expanded: weekend shading from the first weekend day's center through the
 * end of the last weekend day (e.g. Sat mid through end of Sun).
 * Collapsed: from the first weekend day's grid mid-line (green/black) through
 * the end of the last weekend day (e.g. May 2 green through end of May 3).
 */
export function getWeekendCellBackground(
  columnDay: number,
  colSpan: number,
  month: MonthDescriptor,
  monthIndex: number,
  months: MonthDescriptor[],
  isExpanded: boolean,
  isCompact: boolean,
  dayWidthPx: number = DAY_WIDTH_PX
): string | undefined {
  if (!isExpanded) {
    const ranges = getCollapsedWeekendRangesForCell(
      columnDay,
      colSpan,
      month,
      monthIndex,
      months,
      isCompact,
      dayWidthPx
    );
    return buildGradientFromRanges(ranges);
  }

  const zones = getDayZones(
    columnDay,
    colSpan,
    month,
    isExpanded,
    isCompact,
    dayWidthPx
  );
  const prevDayWasWeekend = isPreviousDayWeekend(
    zones[0].day,
    month,
    monthIndex,
    months
  );
  const ranges = getWeekendRangesForZones(
    zones,
    prevDayWasWeekend,
    month,
    monthIndex,
    months
  );

  return buildGradientFromRanges(ranges);
}
